import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Container,
  Alert,
  Skeleton,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { themeColors } from "../../utils/theme";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    try {
      setError("");
      setLoading(true);
      const result = await login(email, password);

      if (result.isSignedIn) {
        navigate("/");
      } else {
        setError("Login failed. Please check your credentials.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to log in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <img
                src="/DN Logo_1024.png"
                alt="Distro Nation Logo"
                width={100}
                height={100}
                style={{ objectFit: "contain" }}
              />
            </Box>
            <Typography component="h1" variant="h5">
              Log In
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 1, width: "100%" }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, bgcolor: themeColors.primary }}
              disabled={loading}
            >
              {loading ? (
                <Skeleton 
                  variant="text" 
                  width="60px" 
                  height={24}
                  sx={{
                    bgcolor: `${themeColors.background}40`,
                    '&::after': {
                      background: `linear-gradient(90deg, transparent, ${themeColors.background}60, transparent)`,
                    },
                  }}
                />
              ) : (
                "Log In"
              )}
            </Button>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Link
                to="/forgot-password"
                style={{ textDecoration: "none", color: themeColors.primary }}
              >
                Forgot Password?
              </Link>
              <Link
                to="/register"
                style={{ textDecoration: "none", color: themeColors.primary }}
              >
                Need an account? Register
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
